import { useCallback, useEffect, useState } from "react";
import { DialogContent, DialogContentText, Grid } from "@mui/material";

import Popup from "../../../../components/common/model/Popup";
import useStyles from "../../../../components/common/model/styles";
import FormField from "../../../../components/common/FormField";

import closeIcon from "../../../../assets/closeModel.png";
import checkedIcon from "../../../../assets/check-mark.png";
import deleteIcon from "../../../../assets/trash.png";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import { FORM_UNIT_TYPE_ELEMENT_FIELDS } from "./unitAllFields";
import Validation from "../../../../utils/Validation";
import {
  addUnitTypeAction,
  initialUnitTypeLocalState,
} from "../../../../store/reducers/storageSettingSlice";
import ResetFormAlert from "../../Communication/ResetFormAlert";
import { mapSelectDataToUnitType, NOTES_EXPAND_THRESHOLD, NOTES_FIELD_ID, UnitType,ALWAYS_ALLOWED_KEY_CODES } from "./unit.type";
import { useDispatch } from "react-redux";
import Notification from "../../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";

interface ManageUnitTypeProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectData?: any;
  isSmallScreen?: boolean;
}

const ManageUnitType = ({
  open,
  onHide,
  title,
  selectData,
}: ManageUnitTypeProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [unitTypeDetails, setUnitTypeDetails] = useState<UnitType>(
    initialUnitTypeLocalState,
  );
  const [alerModeShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);
  const [isActionCalled, setIsActionCalled] = useState(false);
  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation({
    unitTypeName: unitTypeDetails.unitTypeName,
    storageType: unitTypeDetails.storageType,
    sizeFt3: unitTypeDetails.sizeFt3,
    quantity: unitTypeDetails.quantity,
    // unitNumbering: unitTypeDetails.unitNumbering,
    prefix: unitTypeDetails.prefix,
    pricePerUnit: unitTypeDetails.pricePerUnit,
    unitWidth: unitTypeDetails.unitWidth,
    unitLength: unitTypeDetails.unitLength,
    unitHeight: unitTypeDetails.unitHeight,
    // security_deposit: unitTypeDetails.security_deposit,
  });

  // Populate the form whenever a record is selected for editing.
  useEffect(() => {
    if (selectData) {
      setUnitTypeDetails(mapSelectDataToUnitType(selectData));
    }
  }, [selectData]);

  const handleInputChange = (field: string, value: any) => {
    console.log("field", field, value);

    setUnitTypeDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    resetValidator(field);
  };

  const onBlurAction = () => {};

  const handleClear = useCallback(() => {
    setUnitTypeDetails(
      selectData?.id
        ? mapSelectDataToUnitType(selectData)
        : initialUnitTypeLocalState,
    );
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [selectData, resetAllValidator, dispatch]);

  const hideHandler = useCallback(() => {
    handleClear();
    onHide();
  }, [handleClear, onHide]);

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isClickReset) {
      handleClear();
    }
  }, [isClickReset, handleClear]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (!isActionCalled) return;

    if (!isError() && error !== null) {
      const payload = {
        ...unitTypeDetails,
        sizeFt3: Number(unitTypeDetails.sizeFt3),
        unitWidth: Number(unitTypeDetails.unitWidth) ?? null,
        unitLength: Number(unitTypeDetails.unitLength) ?? null,
        unitHeight: Number(unitTypeDetails.unitHeight) ?? null,
        pricePerUnit: Number(unitTypeDetails.pricePerUnit) ?? null,
        quantity: Number(unitTypeDetails.quantity) ?? null,
        securityDepositValue:
          Number(unitTypeDetails.securityDepositValue) ?? null,
      };
      dispatch(addUnitTypeAction(payload));
    }
    setIsActionCalled(false);
  }, [isActionCalled, isError, error, unitTypeDetails, dispatch]);

  /**
   * Restricts keystrokes to a field's `patternAllowed` regex while
   * always letting navigation/editing keys (backspace, tab, arrows, etc.)
   * and ctrl-combinations through.
   */
  const handleFieldKeyDown = (field: any) => (event: any) => {
    if (
      ALWAYS_ALLOWED_KEY_CODES.includes(event.keyCode) ||
      event.ctrlKey
    ) {
      return;
    }
 
    if (field?.patternAllowed && !field.patternAllowed.test(event.key)) {
      event.preventDefault();
      return;
    }
 
    if (
      field?.patternAllowed &&
      !field.patternAllowed.test(event.target.value + event.key)
    ) {
      event.preventDefault();
    }
  };
 
  const handleFieldPaste = (field: any) => (event: any) => {
    if (field?.id !== "type_name") {
      event.preventDefault();
    }
  };
 
  const getFieldInputProps = (field: any) => {
    const fieldValue = unitTypeDetails[field.id as keyof UnitType];
    const isExpandedNotes =
      field?.id === NOTES_FIELD_ID &&
      (fieldValue ? String(fieldValue).length : 0) > NOTES_EXPAND_THRESHOLD;
 
    return {
      // Spread existing inputProps if defined
      ...(field?.inputProps || {}),
      
      // ✅ CONDITIONAL maxLength: Only apply if defined in field config
      ...(field?.maxLength && { maxLength: field.maxLength }),
      
      sx: {
        height: isExpandedNotes ? "auto" : "36px",
        padding: field?.id === NOTES_FIELD_ID ? "4px" : "",
      },
    };
  };
 
  /**
   * Renders a bordered grid of FormFields for a given form section
   * (e.g. "unitDetails" or "dimensions"), avoiding duplicated markup
   * between sections.
   */
  const renderFieldSection = (section: string) => {
    const fields = FORM_UNIT_TYPE_ELEMENT_FIELDS.filter((field) => {
      if (field.section !== section) return false;
      if (field.dependsOn) {
        return (
          unitTypeDetails[field.dependsOn.field as keyof UnitType] ===
          field.dependsOn.value
        );
      }
      return true;
    });
 
    return (
      <Grid
        container
        spacing={0.5}
        sx={{
          border: "1px solid #f3f3f3",
          background: "#f4f4f4",
          p: 2,
          borderRadius: "5px",
          mt: 1,
        }}
      >
        {fields.map((field) => (
          <FormField
            key={field.id}
            {...field}
            inSingleGridForm
            isNonMarginTop
            value={unitTypeDetails[field.id as keyof UnitType] ?? ""}
            error={!!error?.[field?.id]}
            InputProps={getFieldInputProps(field)}
            onBlur={onBlurAction}
            onChange={(e: any) => handleInputChange(field.id, e.target.value)}
            onPaste={handleFieldPaste(field)}
            onKeyDown={handleFieldKeyDown(field)}
          />
        ))}
      </Grid>
    );
  };
 
  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth
        maxWidth="md"
        popupBg="#fff"
        aria-describedby="alert-dialog-slide-description"
        header={
          <>
            <img
              src={checkedIcon}
              alt="save"
              className={classes.checkicons}
              onClick={(e: any) => handleSubmit(e)}
            />

            <img
              src={deleteIcon}
              alt="delete"
              className={classes.deleteicons}
              onClick={openClearPopup}
            />

            <img
              src={closeIcon}
              alt="close"
              className={classes.closeicons}
              onClick={hideHandler}
            />
          </>
        }
      >
        <DialogContent sx={{ p: "16px 10px !important" }}>
          <DialogContentText component="div">
            <Notification />
           <div style={{ display: "flex", gap: "24px" }}>
              <div style={{ flex: 1 }}>
                <h4>Unit Details</h4>
                {renderFieldSection("unitDetails")}
 
                <h4>Dimensions (per unit)</h4>
                {renderFieldSection("dimensions")}
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alerModeShow && (
        <ResetFormAlert
          open={alerModeShow}
          title="Alert"
          alertModalShow={alerModeShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageUnitType;
